import {
  DEAL_PAGE_SIZE,
  DEAL_SORTS,
  NO_DEAL_OWNER,
  type DealBoardQuery,
  type DealListQuery,
  type DealSort,
} from '../contract';
import {
  isoDay,
  PERIOD_OPTIONS,
  periodRange,
  type DayRange,
  type PeriodOption,
} from '@web/shared/util/period';

/**
 * The state of the deals screens in the URL (spec 005, §7.2–7.3), in English:
 * `?pipeline=&display=list&q=&owner=&unit=&team=&tag=&category=&valueMin=&valueMax=&period=&from=&to=&rotting=true&archived=true&sort=&page=`
 * and `&noNextTask=true` (without an open task, docs/specs/006 rule 17). `period` is when the won
 * and lost deals closed (this month when absent; `custom` takes `from` and `to`); open deals
 * show whatever their dates. Values are in whole reais in the URL (people
 * read it) and cents in the API. `owner` takes a membership id, `none` or `me`. The view is
 * `display`, not `view`: `view` belongs to the settings window, open over any screen.
 */
export const DEAL_VIEWS = ['kanban', 'list'] as const;
export type DealView = (typeof DEAL_VIEWS)[number];

/** The won and lost deals show those closed this month unless another period is chosen. */
export const DEFAULT_DEAL_PERIOD: PeriodOption = 'thisMonth';

export const OWNER_ME = 'me';

export interface DealsSearch {
  pipeline?: string;
  /** `proposals`: the proposals of the pipeline's deals (spec 030). */
  display?: 'list' | 'proposals';
  q?: string;
  owner?: string;
  unit?: string;
  team?: string;
  tag?: string;
  /** Deals with an item of this catalog category (QA-04). */
  category?: string;
  /** Whole reais. */
  valueMin?: number;
  valueMax?: number;
  /** When the won and lost deals closed; absent is `DEFAULT_DEAL_PERIOD`. */
  period?: PeriodOption;
  /** The days of a `custom` period (`YYYY-MM-DD`). */
  from?: string;
  to?: string;
  rotting?: true;
  archived?: true;
  /** Without a next activity (no open task; docs/specs/006, rule 17). */
  noNextTask?: true;
  sort?: DealSort;
  page?: number;
}

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

function text(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

function wholeNumber(value: unknown): number | undefined {
  const number = Number(value);
  return value !== undefined && value !== '' && Number.isInteger(number) && number >= 0
    ? number
    : undefined;
}

const isTrue = (value: unknown) => value === true || value === 'true';

/** Reads the state from the URL; anything malformed is dropped (never an error page). */
export function parseDealsSearch(raw: Record<string, unknown>): DealsSearch {
  const search: DealsSearch = {};
  for (const key of ['pipeline', 'q', 'owner', 'unit', 'team', 'tag', 'category'] as const) {
    const value = text(raw[key]);
    if (value) search[key] = value;
  }
  if (raw['display'] === 'list' || raw['display'] === 'proposals') search.display = raw['display'];
  const valueMin = wholeNumber(raw['valueMin']);
  if (valueMin !== undefined) search.valueMin = valueMin;
  const valueMax = wholeNumber(raw['valueMax']);
  if (valueMax !== undefined) search.valueMax = valueMax;
  if (isOneOf(PERIOD_OPTIONS, raw['period']) && raw['period'] !== DEFAULT_DEAL_PERIOD) {
    const from = text(raw['from']);
    const to = text(raw['to']);
    const days = /^\d{4}-\d{2}-\d{2}$/;
    if (raw['period'] !== 'custom') search.period = raw['period'];
    else if (from && to && days.test(from) && days.test(to) && from <= to) {
      Object.assign(search, { period: 'custom', from, to });
    }
  }
  if (isTrue(raw['rotting'])) search.rotting = true;
  if (isTrue(raw['archived'])) search.archived = true;
  if (isTrue(raw['noNextTask'])) search.noNextTask = true;
  if (isOneOf(DEAL_SORTS, raw['sort'])) search.sort = raw['sort'];
  const page = Number(raw['page']);
  if (Number.isInteger(page) && page > 1) search.page = page;
  return search;
}

/** The days of the period the won and lost deals closed in. */
export function closedDays(search: DealsSearch, now: Date = new Date()): DayRange {
  if (search.period === 'custom' && search.from && search.to) {
    return { from: search.from, to: search.to };
  }
  const named = search.period && search.period !== 'custom' ? search.period : DEFAULT_DEAL_PERIOD;
  return periodRange(named, now) ?? { from: isoDay(now), to: isoDay(now) };
}

/** The period of the screen, as the period select shows it. */
export function dealPeriod(search: DealsSearch, now: Date = new Date()) {
  return { period: search.period ?? DEFAULT_DEAL_PERIOD, range: closedDays(search, now) };
}

/** Another period: the default leaves the URL, a custom one keeps its days. */
export function withPeriod(
  search: DealsSearch,
  value: { period: PeriodOption; range: DayRange },
): DealsSearch {
  if (value.period === DEFAULT_DEAL_PERIOD) {
    return withFilters(search, { period: undefined, from: undefined, to: undefined });
  }
  return value.period === 'custom'
    ? withFilters(search, { period: 'custom', from: value.range.from, to: value.range.to })
    : withFilters(search, { period: value.period, from: undefined, to: undefined });
}

/** The filters shared by the board, the list and the leads inbox. */
function filters(search: DealsSearch, membershipId: string | undefined, now: Date) {
  const owner = search.owner === OWNER_ME ? membershipId : search.owner;
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(owner ? { ownerMembershipId: owner } : {}),
    ...(search.unit ? { unitId: search.unit } : {}),
    ...(search.team ? { teamId: search.team } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.category ? { itemCategoryId: search.category } : {}),
    ...(search.valueMin !== undefined ? { valueMin: search.valueMin * 100 } : {}),
    ...(search.valueMax !== undefined ? { valueMax: search.valueMax * 100 } : {}),
    ...(() => {
      const days = closedDays(search, now);
      return { closedFrom: days.from, closedTo: days.to };
    })(),
    ...(search.rotting ? { rotting: true } : {}),
    ...(search.noNextTask ? { nextTask: 'none' as const } : {}),
  };
}

export function toBoardQuery(
  search: DealsSearch,
  pipelineId: string,
  membershipId: string | undefined,
  now: Date = new Date(),
): DealBoardQuery {
  return { pipelineId, ...filters(search, membershipId, now) };
}

export function toListQuery(
  search: DealsSearch,
  pipelineId: string | undefined,
  membershipId: string | undefined,
  now: Date = new Date(),
): DealListQuery {
  return {
    ...(pipelineId ? { pipelineId } : {}),
    ...filters(search, membershipId, now),
    ...(search.archived ? { archived: true } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    page: search.page ?? 1,
    pageSize: DEAL_PAGE_SIZE,
  };
}

/** The leads inbox: deals in the first open stage, across the pipelines (spec rule 24). */
export function toLeadsQuery(
  search: DealsSearch,
  membershipId: string | undefined,
  now: Date = new Date(),
): DealListQuery {
  return {
    ...toListQuery(search, search.pipeline, membershipId, now),
    leads: true,
  };
}

/** Whether anything narrows the deals (search or a filter; the pipeline and view do not). */
export function hasActiveFilters(search: DealsSearch): boolean {
  return Object.entries(search).some(
    ([key, value]) =>
      value !== undefined && !['pipeline', 'display', 'sort', 'page', 'from', 'to'].includes(key),
  );
}

/** The state without search and filters (pipeline, view and sort stay). */
export function clearedSearch(search: DealsSearch): DealsSearch {
  const next: DealsSearch = {};
  if (search.pipeline) next.pipeline = search.pipeline;
  if (search.display) next.display = search.display;
  if (search.sort) next.sort = search.sort;
  return next;
}

/** The filters of the filters panel, in the order their chips show (not the search box). */
export const PANEL_FILTERS = [
  'period',
  'owner',
  'unit',
  'team',
  'tag',
  'category',
  'value',
  'rotting',
  'noNextTask',
  'archived',
] as const;
export type PanelFilter = (typeof PANEL_FILTERS)[number];

/** The panel filters that are on, in chip order (`value` stands for the whole range). */
export function activePanelFilters(search: DealsSearch): PanelFilter[] {
  return PANEL_FILTERS.filter((key) =>
    key === 'value'
      ? search.valueMin !== undefined || search.valueMax !== undefined
      : search[key] !== undefined,
  );
}

/** Turns one panel filter off (the page goes back to the first one). */
export function withoutFilter(search: DealsSearch, key: PanelFilter): DealsSearch {
  if (key === 'value') return withFilters(search, { valueMin: undefined, valueMax: undefined });
  if (key === 'period') {
    return withFilters(search, { period: undefined, from: undefined, to: undefined });
  }
  return withFilters(search, { [key]: undefined });
}

/** The state without the panel filters: the search text, pipeline, view and sort stay. */
export function clearedPanelFilters(search: DealsSearch): DealsSearch {
  const next = clearedSearch(search);
  if (search.q) next.q = search.q;
  return next;
}

/** Changes some filters: the page goes back to the first one. */
export function withFilters(search: DealsSearch, changes: Partial<DealsSearch>): DealsSearch {
  const next: DealsSearch = { ...search, ...changes };
  delete next.page;
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined || value === '') delete next[key as keyof DealsSearch];
  }
  return next;
}

export { NO_DEAL_OWNER };
